import { z } from 'zod'
import _ from 'lodash'
import { timestamp } from '../util'

export enum LabInputType {
	Unspecified = 'unspecified',
	Codes = 'codes',
	Check = 'check',
	BMD = 'bmd',
}

export enum BMDLabInterpretation {
	LowRisk = 'lowRisk',
	IntermediateRisk = 'intermediateRisk',
	HighRisk = 'highRisk',
}

export const RLabRecommendationItem = z.object({
	lab_guid: z.string(),
	labname: z.string(),
	required: z.boolean(),
	updatedat: z.string(),
	validation_regex: z.optional(z.string()),
	value_type: z.optional(z.enum(LabInputType)),
	declinereasons: z.optional(z.string()),
})
export type LabRecommendationItem = z.infer<typeof RLabRecommendationItem>

export const RLabResultItem = z.object({
	labguid: z.string(),
	result: z.optional(z.string()),
	resultdetail: z.optional(z.string()),
	updatedat: z.string(),
	ICD10: z.optional(z.array(z.object({
		Code: z.string(),
		Title: z.string(),
	}))),
})
export type LabResultItem = z.infer<typeof RLabResultItem>

export enum LabActionType {
	Codes = LabInputType.Codes,
	Check = LabInputType.Check,
	BMD = LabInputType.BMD,
	Decline = 'decline',
}

// signify the user has entered a labcode for the item (LabInputType.Codes)
export const RLabItemCodesAction = z.object({
	type: z.literal(LabActionType.Codes),
	code: z.string(),
})
export type LabItemCodesAction = z.infer<typeof RLabItemCodesAction>

// signify the user has performed the item (LabInputType.Check)
// i.e. ocular camera
export const RLabItemCheckAction = z.object({
	type: z.literal(LabActionType.Check),
	performed: z.boolean(),
})

export const RLabItemDeclineAction = z.object({
	type: z.literal(LabActionType.Decline),
	reason: z.string(),
})

export const RLabItemBMDAction = z.object({
	type: z.literal(LabActionType.BMD),
	densityIndex: z.string(),
	corticalBoneThickness: z.string(),
	interpretation: z.string(),
})
export type LabItemBMDAction = z.infer<typeof RLabItemBMDAction>

export const newLabItemBMDAction = (): LabItemBMDAction => ({
	type: LabActionType.BMD,
	densityIndex: '',
	corticalBoneThickness: '',
	interpretation: '',
})

export const RLabItemAction = z.nullable(z.union([RLabItemCodesAction, RLabItemCheckAction, RLabItemDeclineAction, RLabItemBMDAction]))
export type LabItemAction = z.infer<typeof RLabItemAction>

export const RLabItem = z.object({
	action: RLabItemAction,
	labguid: z.string(),
	labname: z.string(),
	result: z.nullable(z.string()),
	resultdetail: z.nullable(z.string()),
	required: z.boolean(),
	updatedat: z.string(),
	ICD10: z.array(z.object({
		Code: z.string(),
		Title: z.string(),
	})),
})
export type LabItem = z.infer<typeof RLabItem>

export const importLabRecommendations = (recommendations: LabRecommendationItem[], labs: LabItem[]): LabItem[] => {
	const labs2: LabItem[] = _.cloneDeep(labs)
	for(let reco of recommendations) {
		const labIdx = labs.findIndex(lab => lab.labguid === reco.lab_guid)
		if(labIdx >= 0) {
			const lab1 = labs[labIdx]
			const lab2 = _.cloneDeep(lab1)
			lab2.labname = reco.labname
			lab2.required = reco.required
			if(!_.isEqual(lab1, lab2)) {
				lab2.updatedat = timestamp()
				labs2[labIdx] = lab2
			}
		} else {
			const lab2: LabItem = {
				action: null,
				labguid: reco.lab_guid,
				labname: reco.labname,
				result: null,
				resultdetail: null,
				required: reco.required,
				updatedat: timestamp(),
				ICD10: [],
			}
			labs2.push(lab2)
		}
	}
	return labs2
}

export const importLabResults = (results: LabResultItem[], labs: LabItem[]): LabItem[] => {
	const labs2: LabItem[] = _.cloneDeep(labs)
	for(let result of results) {
		const labIdx = labs.findIndex(lab => lab.labguid === result.labguid)
		if(labIdx >= 0) {
			const lab1 = labs[labIdx]
			const lab2 = _.cloneDeep(lab1)
			lab2.result = result.result ?? null
			lab2.resultdetail = result.resultdetail ?? null
			lab2.ICD10 = result.ICD10 ?? []
			if(!_.isEqual(lab1, lab2)) {
				lab2.updatedat = timestamp()
				labs2[labIdx] = lab2
			}
		} else {
			// 20250129 Dan has asked to silently disregard all results
			// with non-matching GUIDs (which shouldn't happen... lol)
		}
	}
	return labs2
}
